import React, { useState, useEffect, useRef } from 'react';
import { Lock, Check, Clock, Lightbulb } from 'lucide-react';
import styles from './form.module.css';
import ParticipantQuestionImage from './ParticipantQuestionImage';

function answersEqual(a, b) {
    if (!a || !b) return !a && !b;
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const k of keys) {
        if (Number(a[k]) !== Number(b[k])) return false;
    }
    return true;
}

const QuizForm = ({ content, onSubmit, isPreview = false, isLocked = false, userAnswer = null, allowUpdateResponse = true, activityId = null }) => {
    const questions = content.questions || [];
    const [qIdx, setQIdx] = useState(0);
    const [answers, setAnswers] = useState(() => (userAnswer?.answers && typeof userAnswer.answers === 'object' ? { ...userAnswer.answers } : {}));
    const [timeLeft, setTimeLeft] = useState(null);
    const [timeUp, setTimeUp] = useState(false);
    const timerRef = useRef();
    const nextHandlerRef = useRef();
    const timeUpTimeoutRef = useRef();
    const timeUpScheduledRef = useRef(false);
    // Declared early (not just derived near the JSX) because the timer effects below need it
    // in their dependency arrays — once the whole quiz has been submitted there is nothing
    // left to time, so those effects must stop running instead of continuing to count down
    // (and potentially auto-advancing via handleNext) on a screen the participant is just
    // reviewing.
    const hasSubmitted = userAnswer != null;
    // The optional per-question hint (content.questions[i].description) makes the question
    // easier if the admin chose to add one — it shouldn't just sit there answering the
    // question for anyone who hasn't asked for help yet. Hidden by default, per question.
    const [showHint, setShowHint] = useState(false);
    useEffect(() => { setShowHint(false); }, [qIdx]);

    useEffect(() => {
        if (userAnswer?.answers && typeof userAnswer.answers === 'object') {
            setAnswers({ ...userAnswer.answers });
        } else if (userAnswer == null) setAnswers({});
    }, [userAnswer]);

    const q = questions[qIdx];
    const hasTimer = content.settings?.timer;
    const timerSecs = Number(q?.timerSeconds ?? content.settings?.timerSeconds ?? 30);

    const handleNext = () => {
        if (optionsDisabled) return;
        timeUpScheduledRef.current = false;
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        if (timeUpTimeoutRef.current) {
            clearTimeout(timeUpTimeoutRef.current);
            timeUpTimeoutRef.current = null;
        }
        setTimeUp(false);
        try { localStorage.removeItem(`lp_quiz_deadline_${activityId}_q${qIdx}`); } catch { /* ignore */ }
        if (qIdx < questions.length - 1) {
            setQIdx(i => i + 1);
        } else {
            onSubmit({ answers });
        }
    };

    nextHandlerRef.current = handleNext;

    useEffect(() => {
        if (!hasTimer || hasSubmitted) {
            // No countdown once the whole quiz is submitted — clear anything left running
            // instead of leaving a stale timer chip ticking (or hitting 0 and calling
            // handleNext) on a screen the participant is just reviewing/can't act on.
            if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
            setTimeLeft(null);
            setTimeUp(false);
            return;
        }
        timeUpScheduledRef.current = false;
        setTimeUp(false);

        // Anchor the countdown to a persisted absolute deadline (survives a page reload)
        // instead of always minting a fresh timerSecs-length countdown on mount — otherwise
        // a participant could just refresh the page to get unlimited extra time per question.
        // Uses localStorage (not sessionStorage) so the deadline is also shared with a NEW
        // tab opened to the same join link — sessionStorage alone would let a participant
        // dodge the timer entirely just by opening a second tab.
        const deadlineKey = `lp_quiz_deadline_${activityId}_q${qIdx}`;
        let deadline = null;
        try {
            const saved = localStorage.getItem(deadlineKey);
            if (saved) deadline = Number(saved);
        } catch { /* localStorage unavailable — falls through to a fresh deadline below */ }
        if (!deadline || !Number.isFinite(deadline)) {
            deadline = Date.now() + timerSecs * 1000;
            try { localStorage.setItem(deadlineKey, String(deadline)); } catch { /* ignore */ }
        }

        const tick = () => {
            const remaining = Math.max(0, Math.round((deadline - Date.now()) / 1000));
            setTimeLeft(remaining);
            if (remaining <= 0 && timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
        tick();
        timerRef.current = setInterval(tick, 1000);
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            if (timeUpTimeoutRef.current) clearTimeout(timeUpTimeoutRef.current);
        };
    }, [qIdx, hasTimer, timerSecs, activityId, hasSubmitted]);

    useEffect(() => {
        if (!hasTimer || hasSubmitted || timeLeft !== 0 || timeUpScheduledRef.current) return;
        timeUpScheduledRef.current = true;
        setTimeUp(true);
        const id = setTimeout(() => {
            timeUpTimeoutRef.current = null;
            nextHandlerRef.current?.();
        }, 400);
        timeUpTimeoutRef.current = id;
        return () => {
            if (timeUpTimeoutRef.current === id) {
                clearTimeout(id);
                timeUpTimeoutRef.current = null;
            }
        };
    }, [hasTimer, timeLeft, hasSubmitted]);

    const handleSelect = (optionIdx) => {
        if (optionsDisabled) return;
        setAnswers(a => ({ ...a, [qIdx]: optionIdx }));
    };

    const optionsDisabled = isLocked || (hasSubmitted && !allowUpdateResponse);
    const allAnswered = questions.length > 0 && questions.every((_, i) => answers[i] !== undefined);
    const isUnchanged = hasSubmitted && answersEqual(answers, userAnswer?.answers);
    const isLast = qIdx === questions.length - 1;
    const submitLabel = !hasSubmitted
        ? (isPreview ? 'Submit' : isLocked ? 'Voting Locked' : (isLast ? 'Submit Quiz' : 'Next →'))
        : !allowUpdateResponse || isUnchanged ? <>Submitted <Check size={14} strokeWidth={3} aria-hidden /></> : (isLast ? 'Update response' : 'Next →');
    const submitDisabled = isPreview || isLocked || answers[qIdx] === undefined || (hasSubmitted && isLast && (!allowUpdateResponse || isUnchanged));

    if (!q) return <div className={styles.question}>No questions configured</div>;

    return (
        <div className={styles.form}>
            {content.settings?.description && content.settings?.descText && (
                <div className={styles.description}>
                    {content.settings.descText}
                </div>
            )}
            {content.settings?.imageUrl && (
                <ParticipantQuestionImage imageUrl={content.settings.imageUrl} alt="Quiz visual" />
            )}
            {q.imageUrl && (
                <ParticipantQuestionImage imageUrl={q.imageUrl} alt={`Question ${qIdx + 1} visual`} />
            )}
            {hasTimer && !hasSubmitted && (timeUp ? (
                <div className={styles.timerChip} role="status" aria-live="polite">Time&apos;s up!</div>
            ) : timeLeft !== null && (
                <div className={styles.timerChip}><Clock size={14} aria-hidden /> {timeLeft}s</div>
            ))}
            <span className={styles.hint}>Question {qIdx + 1} of {questions.length}</span>
            <div className={styles.question}>
                {q.text || `Question ${qIdx + 1}`}
                {isLocked && <span className={styles.lockBadge}><Lock size={12} aria-hidden /> Locked</span>}
            </div>
            {q.description && (
                showHint ? (
                    <div className={styles.quizQuestionDesc}>
                        <Lightbulb size={13} aria-hidden style={{ verticalAlign: '-2px', marginRight: 4 }} />
                        {q.description}
                    </div>
                ) : (
                    <button type="button" className={styles.hintToggleBtn} onClick={() => setShowHint(true)}>
                        <Lightbulb size={13} aria-hidden /> Show hint
                    </button>
                )
            )}
            {(q.options || []).map((opt, oi) => (
                <button
                    key={oi}
                    className={`${styles.option} ${answers[qIdx] === oi ? styles.selected : ''}`}
                    onClick={() => handleSelect(oi)}
                    disabled={optionsDisabled}
                    aria-pressed={answers[qIdx] === oi}
                >
                    <span className={`${styles.indicator} ${styles.radio} ${answers[qIdx] === oi ? styles.checked : ''}`}>
                        {answers[qIdx] === oi && <Check size={12} strokeWidth={3} aria-hidden />}
                    </span>
                    {String.fromCharCode(65 + oi)}. {opt || `Option ${String.fromCharCode(65 + oi)}`}
                </button>
            ))}
            <button
                className={styles.submitBtn}
                onClick={handleNext}
                disabled={submitDisabled}
            >
                {submitLabel}
            </button>
        </div>
    );
};

export default QuizForm;
